import React, { useState } from 'react';
import { X, Send, Loader, Check } from 'lucide-react';

interface SubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (solution: string) => Promise<void>;
}

export function SubmissionModal({ isOpen, onClose, onSubmit }: SubmissionModalProps) {
  const [solution, setSolution] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!solution.trim()) return;
    
    try {
      setIsSubmitting(true);
      setError(null);
      await onSubmit(solution.trim());
      
      // Show success message
      setShowSuccess(true);
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        setSolution('');
        setShowSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit solution');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Show success state
  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-black/90 rounded-lg p-6 w-full max-w-md mx-4 relative border border-white/10">
          <div className="flex flex-col items-center justify-center space-y-4 py-8">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-center">Solution Submitted!</h3>
            <p className="text-gray-400 text-center">
              Your solution has been recorded and will be reviewed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-black/90 rounded-lg p-6 w-full max-w-2xl mx-4 relative border border-white/10">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-bold mb-4">Submit Your Solution</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="solution" className="block text-sm font-medium text-gray-300 mb-2">
              Solution Details
            </label>
            <textarea
              id="solution"
              value={solution}
              onChange={(e) => setSolution(e.target.value)}
              placeholder="Enter your solution details, including any relevant code, documentation, or implementation notes..."
              className="w-full h-64 bg-black/20 border border-white/10 rounded-lg py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent resize-none"
              required
              disabled={isSubmitting}
            />
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 text-red-200 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-black/20 hover:bg-black/30 text-white rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !solution.trim()}
              className={`px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2 ${
                (isSubmitting || !solution.trim()) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Submit Solution</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}